
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { User, Phone, Mail, CheckCircle } from 'lucide-react';
import { useVendorAssignment } from '@/hooks/useVendorAssignment';

interface VendorAssignmentCardProps {
  service: any;
  availableUsers: any[];
  onAssignmentChange: () => void;
}

export const VendorAssignmentCard: React.FC<VendorAssignmentCardProps> = ({
  service,
  availableUsers,
  onAssignmentChange
}) => {
  const { assignVendorToService, unassignVendorFromService, isAssigning } = useVendorAssignment();
  const [selectedVendorId, setSelectedVendorId] = React.useState<string>('');

  const handleAssignVendor = async () => {
    if (!selectedVendorId) return;
    
    try {
      await assignVendorToService(service.id, selectedVendorId);
      setSelectedVendorId('');
      onAssignmentChange();
    } catch (error) {
      console.error('Failed to assign vendor:', error);
    }
  };

  const handleUnassignVendor = async () => {
    try {
      await unassignVendorFromService(service.id);
      onAssignmentChange();
    } catch (error) {
      console.error('Failed to unassign vendor:', error);
    }
  };

  const currentOwner = service.owner;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Vendor Assignment
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {currentOwner ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Assigned
                </Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleUnassignVendor}
                disabled={isAssigning}
              >
                Unassign
              </Button>
            </div>
            
            <div className="bg-gray-50 p-3 rounded-lg space-y-2">
              <div className="font-medium">{currentOwner.display_name}</div>
              {currentOwner.email && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="h-3 w-3" />
                  {currentOwner.email}
                </div>
              )}
              {service.phone && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="h-3 w-3" />
                  {service.phone}
                </div>
              )}
            </div>
            
            <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
              <CheckCircle className="h-3 w-3 inline mr-1" />
              Notification preferences have been automatically configured for this vendor.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-gray-600">
              No vendor assigned to this service.
            </div>
            
            <div className="space-y-2">
              <Select value={selectedVendorId} onValueChange={setSelectedVendorId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a user to assign as vendor" />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex flex-col">
                        <span>{user.display_name}</span>
                        <span className="text-xs text-gray-500">{user.email}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                onClick={handleAssignVendor}
                disabled={!selectedVendorId || isAssigning}
                className="w-full"
              >
                {isAssigning ? 'Assigning...' : 'Assign Vendor'}
              </Button>
            </div>
            
            {service.phone && (
              <div className="text-xs text-gray-500 bg-green-50 p-2 rounded">
                <CheckCircle className="h-3 w-3 inline mr-1" />
                When assigned, the vendor will automatically receive notification preferences 
                with the service phone number: {service.phone}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
